import React, { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import AppsIcon from '@mui/icons-material/Apps';
import BusinessIcon from '@mui/icons-material/Business';
import DescriptionIcon from '@mui/icons-material/Description';
import HomeIcon from '@mui/icons-material/Home';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import StorefrontIcon from '@mui/icons-material/Storefront';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import WidgetsIcon from '@mui/icons-material/Widgets';
import GroupsIcon from '@mui/icons-material/Groups';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SchoolIcon from '@mui/icons-material/School';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import PsychologyIcon from '@mui/icons-material/Psychology';

import axiosServices from 'utils/axios';
import useAppSettings from 'hooks/useAppSettings';
import { buildSsoCallbackUrl } from 'utils/sso';

// Ícone de cada sistema (o mesmo menu do Gix Vendas e do Gix Interativo, que usam Iconify)
const ICONS = {
  gix: HomeIcon,
  empresas: BusinessIcon,
  contratos: DescriptionIcon,
  plantoes: LocalHospitalIcon,
  vendas: StorefrontIcon,
  interativo: VideoCallIcon,
  'gestao-pessoas': GroupsIcon,
  'gestao-financeiro': AccountBalanceWalletIcon,
  'gestao-frota': LocalShippingIcon,
  'gestao-ensino': SchoolIcon,
  'gestao-vistoria': FactCheckIcon,
  ia: PsychologyIcon
};

// Menu "Apps" (entre tela cheia e o usuário): substitui os botões Empresas / Contratos / Plantões
// e inclui os produtos da empresa (Gix Vendas, Gix Interativo). A lista vem de GET /auth/apps.
export default function AppsSection() {
  const theme = useTheme();
  const appSettings = useAppSettings();
  const [anchor, setAnchor] = useState(null);
  const [apps, setApps] = useState([]);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    let alive = true;
    axiosServices
      .get('/auth/apps')
      .then((res) => alive && setApps((res.data?.apps || []).filter((a) => a.key !== 'gix' && !(a.key === 'empresas' && !(a.targets?.length > 1)))))
      .catch(() => alive && setApps([]));
    return () => {
      alive = false;
    };
  }, []);

  if (!apps.length) return null;

  const close = () => {
    setAnchor(null);
    setExpanded(null);
  };

  const openUrl = (url) => {
    window.open(url, '_blank', 'noopener');
    close();
  };

  const openTarget = (app, target) => openUrl(app.sso ? buildSsoCallbackUrl(target.domain) : `https://${target.domain}`);

  // app de um endereço só com login único (Gix Vendas, Gix Interativo): mesmo sso_callback do Gix Antigo
  const ssoFromUrl = (url) => {
    try {
      const u = new URL(url);
      const s = new URL(buildSsoCallbackUrl(u.host));
      s.searchParams.set('redirect_url', url);
      return s.toString();
    } catch {
      return url;
    }
  };

  const pick = (app) => {
    const targets = app.targets || [];
    if (targets.length > 1) return setExpanded(expanded === app.key ? null : app.key);
    if (targets.length === 1) return openTarget(app, targets[0]);
    if (app.url) openUrl(app.sso ? ssoFromUrl(app.url) : app.url);
  };

  const current = apps.find((a) => a.key === expanded);

  return (
    <>
      <Tooltip title="Escolha uma aplicação">
        <ButtonBase
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-label="Apps"
          sx={{
            ml: 1,
            height: 40,
            px: 1.5,
            gap: 0.75,
            borderRadius: '20px',
            fontWeight: 600,
            fontSize: '0.8125rem',
            color: appSettings?.navbar_font_color || theme.palette.primary.main,
            bgcolor: appSettings?.navbar_icon_bg_color || theme.palette.primary.light,
            transition: 'all .2s ease-in-out',
            '&:hover': { bgcolor: theme.palette.primary.main, color: theme.palette.primary.contrastText }
          }}
        >
          <AppsIcon sx={{ fontSize: 20 }} />
          Apps
        </ButtonBase>
      </Tooltip>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 300, p: 1.5 } } }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ px: 0.5, fontWeight: 600 }}>
          Escolha uma aplicação
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.5, mt: 1 }}>
          {apps.map((app) => {
            const Icon = ICONS[app.key] || WidgetsIcon;
            const active = expanded === app.key;
            return (
              <ButtonBase
                key={app.key}
                onClick={() => pick(app)}
                sx={{
                  flexDirection: 'column',
                  gap: 0.75,
                  p: 1.25,
                  borderRadius: 1.5,
                  color: active ? 'primary.main' : 'text.primary',
                  bgcolor: active ? theme.palette.primary.light : 'transparent',
                  '&:hover': { bgcolor: theme.palette.primary.light, color: 'primary.main' }
                }}
              >
                <Icon />
                <Typography variant="caption" sx={{ textAlign: 'center', lineHeight: 1.2 }}>
                  {app.name}
                </Typography>
              </ButtonBase>
            );
          })}
        </Box>

        {current?.targets?.length > 1 ? (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography variant="caption" color="text.secondary" sx={{ px: 0.5, fontWeight: 600 }}>
              Empresas: escolha a empresa
            </Typography>
            <List dense disablePadding>
              {current.targets.map((t) => (
                <ListItemButton key={`${t.id}-${t.domain}`} onClick={() => openTarget(current, t)}>
                  <ListItemText primary={t.name} />
                </ListItemButton>
              ))}
            </List>
          </>
        ) : null}
      </Popover>
    </>
  );
}
